import type { CustomCsvImportCapabilities } from "../services/brokerIntegrationApi";
import type {
  CsvImportMappableField,
  ImportMode,
  LayoutRowGrouping,
  LayoutRowGroupingId,
  LayoutShape,
  LayoutShapeId,
  ValueTransforms,
} from "../types/customCsvImport";
import {
  FALLBACK_LAYOUT_ROW_GROUPINGS,
  FALLBACK_LAYOUT_SHAPES,
} from "../components/Broker/CustomCsvImport/layoutShapeCatalog";

export function layoutShapeExampleRows(
  shape: Pick<LayoutShape, "example_headers" | "example_rows">
): Array<Record<string, string>> {
  return shape.example_rows.map((row) =>
    Object.fromEntries(shape.example_headers.map((header, index) => [header, row[index] ?? ""]))
  );
}

export function resolveLayoutRowGroupings(
  csvImport: CustomCsvImportCapabilities | null
): LayoutRowGrouping[] {
  const fromApi = csvImport?.layout_row_groupings;
  if (Array.isArray(fromApi) && fromApi.length > 0) {
    return fromApi;
  }
  return FALLBACK_LAYOUT_ROW_GROUPINGS;
}

export function resolveLayoutShapes(
  csvImport: CustomCsvImportCapabilities | null
): LayoutShape[] {
  const fromApi = csvImport?.layout_shapes;
  if (Array.isArray(fromApi) && fromApi.length > 0) {
    return fromApi;
  }
  return FALLBACK_LAYOUT_SHAPES;
}

export function findLayoutShape(
  csvImport: CustomCsvImportCapabilities | null,
  shapeId: string | undefined | null
): LayoutShape | undefined {
  if (!shapeId) return undefined;
  return resolveLayoutShapes(csvImport).find((s) => s.id === shapeId);
}

export function findLayoutShapesForGrouping(
  csvImport: CustomCsvImportCapabilities | null,
  groupingId: LayoutRowGroupingId
): LayoutShape[] {
  return resolveLayoutShapes(csvImport).filter((s) => s.row_grouping_id === groupingId);
}

export function findLayoutRowGrouping(
  csvImport: CustomCsvImportCapabilities | null,
  groupingId: LayoutRowGroupingId
): LayoutRowGrouping | undefined {
  return resolveLayoutRowGroupings(csvImport).find((g) => g.id === groupingId);
}

export function resolveLayoutShapeLabel(
  csvImport: CustomCsvImportCapabilities | null,
  shapeId: string | undefined | null
): string | null {
  return findLayoutShape(csvImport, shapeId)?.label ?? null;
}

export function resolveFieldLabels(
  fieldIds: string[],
  mappableFields: CsvImportMappableField[]
): string[] {
  const byField = new Map(mappableFields.map((f) => [f.field, f.label || f.field]));
  return fieldIds.map((id) => byField.get(id) ?? id);
}

export function isPerFillShape(shape: LayoutShape | undefined): boolean {
  return shape?.row_grouping_id === "per_fill";
}

const GROUPING_PREVIEW_SHAPE_ID: Record<LayoutRowGroupingId, LayoutShapeId> = {
  single_row: "single_open_close_times",
  paired_rows: "paired_side_open_close",
  per_fill: "fill_side_open_close",
};

/** One illustrative table per row-grouping choice (step 1). */
export function getGroupingPreviewShape(
  csvImport: CustomCsvImportCapabilities | null,
  groupingId: LayoutRowGroupingId
): LayoutShape | undefined {
  const shapes = findLayoutShapesForGrouping(csvImport, groupingId);
  const preferred = GROUPING_PREVIEW_SHAPE_ID[groupingId];
  return shapes.find((s) => s.id === preferred) ?? shapes[0];
}

export function mergeLayoutShapeIntoState(shape: LayoutShape): {
  startSource: "layout";
  layoutShapeId: LayoutShapeId;
  profileSlug: undefined;
  importMode: ImportMode;
  columnMapping: Record<string, never>;
  valueTransforms: ValueTransforms;
  defaults: LayoutShape["defaults"];
  quantityUnit: "units" | "lots";
  preview: null;
  acknowledgedWarningCodes: Set<string>;
} {
  return {
    startSource: "layout",
    layoutShapeId: shape.id,
    profileSlug: undefined,
    importMode: shape.import_mode,
    columnMapping: {},
    valueTransforms: { ...shape.value_transforms },
    defaults: { ...(shape.defaults ?? {}) },
    quantityUnit: shape.defaults?.quantity_unit ?? "units",
    preview: null,
    acknowledgedWarningCodes: new Set(),
  };
}
